/**
 * NotificationItem component - Iteration 6 - Notifications & Sharing.
 * 
 * Displays a single notification with appropriate styling and actions.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { Notification } from '../../types/notification';
import { NotificationType, ReferenceType } from '../../types/notification';
import { formatRelativeTime } from '../../utils/dateUtils';
import { useLanguage } from '../../i18n/useLanguage';
import type { LanguageContextValue } from '../../i18n/LanguageProvider';
import './NotificationItem.css';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onClose: () => void;
}

/**
 * Get icon based on notification type.
 */
const getNotificationIcon = (type: NotificationType): string => {
  switch (type) {
    case NotificationType.NEW_COMMENT:
      return '💬';
    case NotificationType.MENTION:
      return '@';
    case NotificationType.POST_MODERATED:
      return '⚠️';
    case NotificationType.COMMENT_MODERATED:
      return '⚠️';
    case NotificationType.SYSTEM:
      return 'ℹ️';
    default:
      return '📌';
  }
};

function localizeTitle(notification: Notification, t: LanguageContextValue['t']): string {
  if (notification.type === NotificationType.NEW_COMMENT) {
    return t('notification.newComment.title');
  }
  return notification.title;
}

function localizeMessage(notification: Notification, t: LanguageContextValue['t']): string {
  if (notification.type === NotificationType.NEW_COMMENT) {
    const EN_INFIX = ' commented on your post: ';
    const idx = notification.message.indexOf(EN_INFIX);
    if (idx !== -1) {
      const username = notification.message.substring(0, idx);
      const content = notification.message.substring(idx + EN_INFIX.length);
      return `${username} ${t('notification.newComment.infix')} ${content}`;
    }
  }
  return notification.message;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onClose,
}) => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  const handleClick = () => {
    // Mark as read if not already
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }

    // Navigate to the referenced content
    if (notification.referenceId) {
      if (notification.referenceType === ReferenceType.POST) {
        navigate(`/posts/${notification.referenceId}`);
        onClose();
      } else if (notification.referenceType === ReferenceType.COMMENT) {
        // Comments are part of posts, we need the post ID from context
        // For now, just close the dropdown
        onClose();
      }
    }
  };

  const icon = getNotificationIcon(notification.type);
  const timeAgo = formatRelativeTime(notification.createdAt, language);

  return (
    <div 
      className={`notification-item ${notification.read ? 'read' : 'unread'}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleClick()}
    >
      <div className="notification-icon">{icon}</div>
      <div className="notification-content">
        <div className="notification-title">{localizeTitle(notification, t)}</div>
        <div className="notification-message">{localizeMessage(notification, t)}</div>
        <div className="notification-time">{timeAgo}</div>
      </div>
      {!notification.read && <div className="notification-unread-dot" />}
    </div>
  );
};

export default NotificationItem;
